import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Loader2, RotateCcw, CheckCircle2, Building2 } from "lucide-react";
import { BrandLockup } from "../components/BrandLogo";
import { toast } from "sonner";
import api from "../lib/api";

const fmt = (iso) => (iso || "").slice(0, 10);

function ReportRow({ r, onAction, busy }) {
  return (
    <div data-testid={`scam-report-${r.jd_hash.slice(0, 12)}`} className={`rounded-xl border p-5 ${r.cleared ? "border-line bg-canvas opacity-70" : "border-warning/30 bg-surface"}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
        <span className={`px-2.5 py-0.5 rounded-full border font-mono2 uppercase tracking-wider ${r.cleared ? "border-line-strong text-body" : "border-warning/40 text-warning"}`}>
          {r.cleared ? "Cleared" : "Active"}
        </span>
        <span className="text-body">{r.reporters} reporter{r.reporters === 1 ? "" : "s"}</span>
        <span className="text-faint">· {fmt(r.first_at)}{r.last_at !== r.first_at ? ` → ${fmt(r.last_at)}` : ""}</span>
        {r.company && (
          <span className="flex items-center gap-1 text-body ml-auto">
            <Building2 className="w-3.5 h-3.5" /> {r.company}
            <span className="text-faint">({r.company_reporters} reporting this company{r.company_cleared ? ", cleared" : ""})</span>
          </span>
        )}
      </div>
      <p className="text-sm text-body leading-relaxed whitespace-pre-line line-clamp-4">{r.excerpt}</p>
      <div className="flex flex-wrap gap-2 mt-4">
        <button data-testid={`scam-toggle-post-${r.jd_hash.slice(0, 12)}`} disabled={busy} onClick={() => onAction({ jd_hash: r.jd_hash, cleared: !r.cleared })}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-sm border border-line text-ink hover:bg-subtle transition-colors disabled:opacity-50">
          {r.cleared ? <><RotateCcw className="w-4 h-4" /> Restore flag</> : <><CheckCircle2 className="w-4 h-4 text-brand" /> Clear (false flag)</>}
        </button>
        {r.company && (
          <button data-testid={`scam-toggle-company-${r.jd_hash.slice(0, 12)}`} disabled={busy} onClick={() => onAction({ company_norm: r.company, cleared: !r.company_cleared })}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-sm border border-line text-body hover:bg-subtle transition-colors disabled:opacity-50">
            <Building2 className="w-4 h-4" /> {r.company_cleared ? "Allow company warnings again" : "Never warn about this company"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminScamReports() {
  const [rows, setRows] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("active");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/scam-reports");
      setRows(data.reports);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) setDenied(true);
      else toast.error("Couldn't load reports");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (body) => {
    setBusy(true);
    try {
      await api.post("/admin/scam-reports/clear", body);
      toast.success(body.cleared ? "Cleared. Users won't see this warning." : "Warning restored");
      await load();
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy(false);
    }
  };

  const shown = (rows || []).filter((r) => (filter === "all" ? true : filter === "active" ? !r.cleared : r.cleared));

  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid="admin-scam-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-surface/90 border-b border-line">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" aria-label="GetInterviewed home">
            <BrandLockup />
            {rows !== null && <span className="text-xs font-mono2 uppercase tracking-widest text-warning border border-warning/40 rounded-full px-2 py-0.5">Admin</span>}
          </Link>
          <Link to="/dashboard" data-testid="admin-back-dashboard" className="px-4 py-2 rounded-xl border border-line text-sm text-ink hover:bg-subtle transition-colors">Dashboard</Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-10">
        {rows !== null && (
          <>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3"><ShieldAlert className="w-8 h-8 text-warning" /> Scam reports</h1>
            <p className="text-body mt-2 text-sm">Posts users have reported. Clear false flags so others no longer see a warning; you can restore them any time.</p>
          </>
        )}
        {denied ? (
          <div data-testid="admin-denied" className="mt-10 rounded-xl border border-line bg-surface p-8 text-center text-body">This page is for GetInterviewed admins only.</div>
        ) : rows === null ? (
          <div className="mt-10 flex items-center gap-2 text-body"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="flex gap-2 mt-8 mb-5">
              {["active", "cleared", "all"].map((f) => (
                <button key={f} data-testid={`admin-filter-${f}`} onClick={() => setFilter(f)}
                  className={`px-4 py-1.5 rounded-xl text-sm border transition-colors ${filter === f ? "border-brand bg-brand-soft text-brand" : "border-line text-body hover:border-brand/40"}`}>
                  {f[0].toUpperCase() + f.slice(1)} ({(rows || []).filter((r) => (f === "all" ? true : f === "active" ? !r.cleared : r.cleared)).length})
                </button>
              ))}
            </div>
            {shown.length === 0 ? (
              <div data-testid="admin-empty" className="rounded-xl border border-line bg-surface p-8 text-center text-body">No reports here.</div>
            ) : (
              <div className="space-y-4">{shown.map((r) => <ReportRow key={r.jd_hash} r={r} onAction={act} busy={busy} />)}</div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
